import { type Router, type LocationQueryRaw, RouteRecordRaw } from 'vue-router';
// import NProgress from 'nprogress'; // progress bar
import { Modal } from '@arco-design/web-vue';

import { useUserStore, useTabBarStore, useSupplierStore } from '@/store';
import { isLogin } from '@/utils/auth';
import { isLoginBySupplier } from '@/store/modules/supplier/auth';
import {
  getCurrentUser,
  queryCompanyConfigList,
  queryUserConfigList,
  sendExpirationReminder,
} from '@/api/user';
import { thirdpartList } from '@/api/thirdparty';
import { getFlowList } from '@/api/flow';
import {
  SUPPLIER_LOGIN_REDIRECT_ROUTE_NAME,
  DEFAULT_SUPPLIER_ROUTE_NAME,
  SUPPLIER_LOGIN_ROUTE,
  NOT_BOUND_CLIENT_ROUTE_NAME,
  SUPPLIER_LOGIN_ROUTE_NAME,
} from '@/router/constants';
import { getQueryVariable, isDev } from '@/utils/common';
import { supplierLogin, getSupplierConfig } from '@/api/wx';
import useSSE from '@/hooks/useSSE';
import { queryRestDays } from '@/api/product';
import dayjs from 'dayjs';
import { isElectronMainPage } from '@/utils/electronHelper';
import { toGrayUrlForGrayUser, getDmsConfig } from '@/utils/dms';
import globalContants from '@/constants/globalContants';

// 弹框是否正在显示中
let isExpirationReminderShowing = false;

/**
 * 检查是否需要显示软件到期提醒
 * 条件：1. enableEnd 存在且距离到期不足30天 2. 用户有 supplierRecordGet 权限 3. 未弹过框
 */
function checkExpirationReminder(currentUser: any) {
  const enableEnd = currentUser?.data?.enablePaymentServiceMap?.enableEnd;
  const cid = currentUser?.data?.companyInfo?.id;
  const userId = currentUser?.data?.userInfo?.id;

  if (!enableEnd) return;

  // 检查是否正在显示中
  if (isExpirationReminderShowing) return;

  // 检查是否已弹过框（基于 cid + userId + 到期时间戳作为唯一标识，确保每个用户独立弹窗）
  const reminderKey = `SCM_EXPIRATION_REMINDER_${cid}_${userId}_${enableEnd}`;
  if (localStorage.getItem(reminderKey)) return;

  // 计算剩余天数
  const endDate = new Date(enableEnd);
  const now = new Date();
  const diffDays = Math.ceil(
    (endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
  );

  // 大于等于30天或已过期不弹框
  if (diffDays >= 30 || diffDays < 0) return;

  // 检查是否有 supplierRecordGet 权限
  const permissions = currentUser?.data?.permissions || [];
  if (!permissions.includes('supplierRecordGet')) return;

  // 格式化日期
  const formattedDate = dayjs(endDate).format('YYYY-MM-DD');

  // 提示内容
  const reminderContent = `贵公司购买的软件版本将于 ${formattedDate} 到期，到期后将无法登录。请及时联系销售顾问续费避免影响软件使用`;

  // 标记弹框正在显示
  isExpirationReminderShowing = true;

  // 弹出提示框
  Modal.confirm({
    title: '提示',
    content: reminderContent,
    okText: '知道了',
    hideCancel: true,
    maskClosable: false, // 点击遮罩不关闭
    onOk: async () => {
      // 记录已弹框状态
      localStorage.setItem(reminderKey, 'true');
      isExpirationReminderShowing = false;

      // 调用后端接口发送系统消息
      try {
        await sendExpirationReminder({
          enableEnd,
          content: reminderContent,
        });
      } catch (error) {
        console.error('发送到期提醒消息失败:', error);
      }
    },
  });
}

let isGlobalLoading = true;
let electronInit = false;

// 处理路由菜单隐藏逻辑
const handleRouter = (router: Router, options: Recordable) => {
  const { paymentMap } = useTabBarStore();
  const { sysConfigList = [], userInfo } = options;
  const isOpenStockByCloth = !!sysConfigList.find(
    (item: any) => item.configKey === 'stockByCloth'
  )?.enable;

  const routesConfig = router.getRoutes();
  const handleRoute = (routes: any) => {
    const titleMap: any = {
      '/develop': `研发管理${
        paymentMap?.flagShip && paymentMap?.professional ? '（专业版）' : ''
      }`,
      '/develop-pro': `研发管理${
        paymentMap?.flagShip && paymentMap?.professional ? '（旗舰版）' : ''
      }`,
    };
    routes.forEach((route: any) => {
      if (route.path === '/basis/material-type') {
        if (isDev()) {
          route.meta.amisAppId = isOpenStockByCloth ? 'ddcc644b' : '73083127';
        } else {
          route.meta.amisAppId = isOpenStockByCloth ? 'd9496dd1' : 'e3711f89';
        }
      }
      if (titleMap[route.path]) {
        route.meta.title = titleMap[route.path];
      }
      if (
        (!paymentMap?.flagShip && route.path === '/develop-pro') ||
        (!paymentMap?.professional && route.path === '/develop')
      ) {
        route.meta.hideInMenu = true;
      }
      if (route.flagShip && !paymentMap?.flagShip) {
        // 未开启旗舰版
        route.meta.hideInMenu = true;
      }
      // 非主账号隐藏系统重置菜单（accountType: 0-主账号, 1-子账号, 2-微信账号）
      if (route.path === '/basis/system-reset' && userInfo?.accountType !== 0) {
        route.meta.hideInMenu = true;
      }
    });
  };
  handleRoute(routesConfig);
};
const handleGlobalContants = (options: Recordable, cid: number | string) => {
  const { CID = [], ATTACHEMENT_MAX_COUNT, ATTACHEMENT_MAX_SIZE } = options;
  let cids = [];
  if (isDev()) {
    cids = CID.filter((item: string) => item.includes('dev-'));
  } else {
    cids = CID.filter((item: string) => item.includes('prod-'));
  }
  cids = cids.map((item: string) => item.split('-')[1]);
  if (cids.includes(`${cid}`)) {
    globalContants.ATTACHEMENT_MAX_COUNT =
      ATTACHEMENT_MAX_COUNT ?? globalContants.ATTACHEMENT_MAX_COUNT;
    globalContants.ATTACHEMENT_MAX_SIZE =
      ATTACHEMENT_MAX_SIZE ?? globalContants.ATTACHEMENT_MAX_SIZE;
  }
};
const handleDMSConfig = (dmsConfig: Recordable, cid: number | string) => {
  handleGlobalContants(dmsConfig.globalContants ?? {}, cid);
};
export default function setupUserLoginInfoGuard(router: Router) {
  const tabbarStore = useTabBarStore();
  const { checkSSEStatus } = useSSE();
  router.beforeEach(async (to, from, next) => {
    // NProgress.start();
    // 添加静态文件例外规则
    // if (window.location.pathname.includes('weChatJumpToApp.html')) {
    //   console.log(123);

    //   next(window.location.pathname);
    //   return;
    // }
    const userStore = useUserStore();
    tabbarStore.setPageLoading(true);
    if (isLogin()) {
      // 用于缓存已获取的 currentUser，避免重复请求
      let cachedCurrentUser: any = null;
      if (!tabbarStore.paymentMap) {
        cachedCurrentUser = await getCurrentUser();
        tabbarStore.setPayment(cachedCurrentUser);
      }
      // 已经请求了currentUser.ac的接口，刷新后，浏览器PC中userStore.userInfo 是undefined,
      // 但是客户端userStore.userInfo是有正常（旧）值的，必须让客户端走一次获取用户信息的逻辑
      // 用 electronInit 做一个阀门，只修改一次。
      let userInfo = { ...(userStore.userInfo ?? {}) };
      if (isElectronMainPage() && !electronInit) {
        userInfo = {};
        electronInit = true;
      }
      if (Object.keys(userInfo).length !== 0) {
        if (to.name === 'login') {
          // 阻止已经登录的用户直接访问登录页
          next(false);
          return;
        }
        // 检查软件到期提醒（使用已缓存的用户数据）
        if (cachedCurrentUser) {
          checkExpirationReminder(cachedCurrentUser);
        }
        next();
      } else {
        try {
          const [
            currentUser,
            companyConfig,
            userConfig,
            thirdpartyRes,
            flowConfigRes,
            restDaysRes,
            dmsConfigRes,
          ] = await Promise.all([
            cachedCurrentUser
              ? Promise.resolve(cachedCurrentUser)
              : getCurrentUser(),
            queryCompanyConfigList(),
            queryUserConfigList({}),
            thirdpartList().catch((error: any) => {
              console.error('thirdpartList error:', error);
              return {};
            }),
            getFlowList({}),
            queryRestDays({}).catch(() => {
              return {};
            }),
            getDmsConfig().catch(() => {
              return {};
            }),
          ]);
          await toGrayUrlForGrayUser(currentUser.data?.companyInfo.id);
          // 更新第三方对接信息
          userStore.updateThirdparty(thirdpartyRes?.data?.thirdparts ?? []);
          // 更新流程配置信息
          userStore.updateFlowConfig(flowConfigRes?.data?.list ?? []);
          // 更新休息日信息
          userStore.updateRestDays(restDaysRes.data ?? []);
          checkSSEStatus(); // 检查SSE连接
          const { paymentMap } = tabbarStore;
          userStore.setInfo({
            userInfo: currentUser.data?.userInfo,
            permissions: currentUser.data?.permissions,
            companyInfo: currentUser.data?.companyInfo,
            permissionRemarkMap: currentUser.data.permissionRemarkMap,
            sysConfigList: companyConfig.data,
            userConfigList: userConfig.data,
            enablePaymentServiceMap: paymentMap,
          });
          handleDMSConfig(dmsConfigRes ?? {}, currentUser.data?.companyInfo.id);
          // 处理菜单路由隐藏
          handleRouter(router, {
            sysConfigList: companyConfig.data,
            userInfo: currentUser.data?.userInfo,
          });
          // 检查软件到期提醒
          checkExpirationReminder(currentUser);
          next();
        } catch {
          await userStore.logout();
          next({
            name: 'login',
            query: {
              redirect: to.name,
              ...to.query,
            } as LocationQueryRaw,
          });
        }
      }
    } else if (isLoginBySupplier()) {
      // 已登录了加工厂账号
      const supplierStore = useSupplierStore();
      if (Object.keys(supplierStore.supplierInfo ?? {}).length !== 0) {
        if (Object.keys(supplierStore.clientInfo ?? {}).length !== 0) {
          if (supplierStore.sysConfigList.length === 0) {
            const supplierConfig = await getSupplierConfig();
            supplierStore.setInfo({
              sysConfigList: supplierConfig.data?.config,
            });
          }
          if (to.name === SUPPLIER_LOGIN_ROUTE_NAME) {
            // 有账号重定向到首页
            next({ name: DEFAULT_SUPPLIER_ROUTE_NAME });
            return;
          }
          next();
          return;
        }
        if (to.name === NOT_BOUND_CLIENT_ROUTE_NAME) {
          next();
          return;
        }
        // 没绑定品牌方
        next({ name: NOT_BOUND_CLIENT_ROUTE_NAME });
      } else {
        // 未登录加工厂账号
        await supplierStore.logout();
        next(SUPPLIER_LOGIN_ROUTE);
      }
    } else {
      if ([SUPPLIER_LOGIN_ROUTE_NAME, 'login'].includes(to.name as string)) {
        next();
        return;
      }
      if (
        (getQueryVariable('state') as string) ===
        SUPPLIER_LOGIN_REDIRECT_ROUTE_NAME
      ) {
        // 扫码确认登录后，微信服务器的重定向
        const supplierStore = useSupplierStore();
        try {
          // 使用code登录微信账号
          const currentSupplier = await supplierLogin({
            code: getQueryVariable('code'),
          });
          supplierStore.loginCallback(currentSupplier.data);
          const { assistantSupplierList = [] } = currentSupplier.data;
          // 去除哈希#号前多余的字符串并重定向到加工厂系统首页
          const correctUrl = new URL(window.location.href);
          // 拿到路由对应的路径href
          const addHref = router.resolve({
            name: assistantSupplierList.length
              ? DEFAULT_SUPPLIER_ROUTE_NAME
              : NOT_BOUND_CLIENT_ROUTE_NAME,
          });
          // 重定向（没绑定品牌方重定向到未绑定页面）
          window.location = (correctUrl.origin +
            correctUrl.pathname +
            addHref.href) as Location | (string & Location);
          next(false);
        } catch {
          await supplierStore.logout();
          // 跳回加工厂的登录页面
          next(SUPPLIER_LOGIN_ROUTE);
        }
        return;
      }
      // 跳回erp的登录页面
      next({
        name: 'login',
        query: {
          redirect: to.name,
          ...to.query,
        } as LocationQueryRaw,
      });
    }
  });
  router.afterEach(() => {
    if (isGlobalLoading) {
      const globalLoadingDom = document.querySelector('#globalLoading');
      if (globalLoadingDom) {
        globalLoadingDom.remove();
        isGlobalLoading = false;
      }
    }

    setTimeout(() => {
      tabbarStore.setPageLoading(false);
    }, 80);
    // NProgress.done();
  });
}
